import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { RecaptchaServiceInterface } from '@central-products/interface-recaptcha-service';
import { SessionManagerInterface } from '@central-products/interface-session-manager';
import { WebLoginPreferencesInterface } from '@central-products/interface-web-login-preferences';
import { loadMicroApp } from '@central-products/script-module-loader';
import { Overlay } from '@sitebase/ui-kit';
import { loginVisibleCSSAppend, loginVisibleCSSRemove } from './utils/deviceUtils';
import { Environment, Jurisdiction, OnActionRequested } from './types';

export interface LoginContainerProps {
  environment: Environment;
  jurisdiction: Jurisdiction;
  locale: string;
  sessionManager: SessionManagerInterface;
  webLoginPreferences: WebLoginPreferencesInterface;
  onClose: () => void;
  onSubmit: () => void;
  onSubmitFailed: () => void;
  onSubmitSuccess: () => void;
  onActionRequested: OnActionRequested;
  recaptchaService: RecaptchaServiceInterface;
}

interface LoginContainerState {
  isLoading: boolean;
}

export class LoginContainer extends React.Component<LoginContainerProps, LoginContainerState> {
  public readonly state: LoginContainerState = {
    isLoading: true,
  };
  private rootNode: HTMLElement | null = null;

  public componentDidMount() {
    const {
      environment,
      jurisdiction,
      locale,
      sessionManager,
      webLoginPreferences,
      onSubmit,
      onSubmitFailed,
      onSubmitSuccess,
      recaptchaService,
    } = this.props;

    // Overlay for React 15 uses unstable ReactDOM method, which causes issues when un-mounting component.
    // This is a fix for it, we call closing methods after running React lifecycle methods.
    const onClose = () => this.setState({}, this.props.onClose);
    const onActionRequested: OnActionRequested = (eventId, context, isCloseRequired) => {
      this.setState({}, () => this.props.onActionRequested(eventId, context, isCloseRequired));
    };

    window.performance?.mark?.('cp:login:load-bundle');
    loginVisibleCSSAppend();
    loadMicroApp({ name: 'login', environment, jurisdiction, dependencies: { React, ReactDOM } }).then(() => {
      this.setState({ isLoading: false });
      window.WH['cp-apps'].login.open(
        {
          jurisdiction,
          locale,
          sessionManager,
          webLoginPreferences,
          onClose,
          onSubmit,
          onSubmitFailed,
          onSubmitSuccess,
          onActionRequested,
          recaptchaService,
        },
        this.rootNode as HTMLElement
      );
    });
  }

  public componentWillUnmount() {
    loginVisibleCSSRemove();
    window.WH['cp-apps'].login.close();
  }

  public render() {
    return (
      <Overlay className="cp-login-overlay">
        {this.state.isLoading ? <div className="cp-login-overlay__spinner" /> : null}
        <div className="cp-login-overlay__container" ref={this.setRootNodeRef} />
      </Overlay>
    );
  }

  private setRootNodeRef = (node: HTMLElement | null) => {
    this.rootNode = node;
  };
}
